import React, { useState, useEffect } from 'react';
import themeAngleMappingApi from '@services/themeAngleMappingApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const ThemeAngleSelectionModal = ({
    themePageMaster,
    themeId,
    availableAngles = [], 
    onClose, 
    onSave, 
    isEditMode = false, 
    initialData = null 
}) => {
    // 상태 관리
    const [selectedAngleId, setSelectedAngleId] = useState(null);
    const [customDefaultImageResourceId, setCustomDefaultImageResourceId] = useState(null);
    const [customHoverImageResourceId, setCustomHoverImageResourceId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAngleInfo, setSelectedAngleInfo] = useState(null);

    const themePageId = themePageMaster?.id || null;

    // 초기 데이터 설정 (수정 모드)
    useEffect(() => {
        if (isEditMode && initialData) {
            console.log('수정 모드 초기 데이터:', initialData);
            
            setSelectedAngleId(initialData.cameraAngleId);
            setCustomDefaultImageResourceId(initialData.customDefaultImageResourceId);
            setCustomHoverImageResourceId(initialData.customHoverImageResourceId);
            
            // 수정 모드에서는 현재 선택된 앵글 정보를 직접 사용
            if (initialData.angleName) {
                setSelectedAngleInfo({
                    id: initialData.cameraAngleId,
                    angleName: initialData.angleName,
                    angleTypeCode: initialData.angleTypeCode,
                    angleTypeDescription: initialData.angleTypeDescription
                });
            } else {
                // availableAngles에서 찾기 시도
                const angleInfo = availableAngles.find(angle => angle.id === initialData.cameraAngleId);
                if (angleInfo) {
                    setSelectedAngleInfo(angleInfo);
                }
            }
        }
    }, [isEditMode, initialData, availableAngles]);

    // 디버깅을 위한 상태 추적
    useEffect(() => {
        console.log('현재 상태:', {
            selectedAngleId,
            customDefaultImageResourceId,
            customHoverImageResourceId,
            selectedAngleInfo
        });
    }, [selectedAngleId, customDefaultImageResourceId, customHoverImageResourceId, selectedAngleInfo]);

    // 앵글 선택 핸들러
    const handleAngleSelect = (angleId) => {
        setSelectedAngleId(angleId);
        const angleInfo = availableAngles.find(angle => angle.id === angleId);
        setSelectedAngleInfo(angleInfo);
    };

    // 리소스 업로드 핸들러
    const handleResourceUploaded = (resourceType, resourceId) => {
        console.log(`리소스 업로드: ${resourceType} = ${resourceId}`);
        if (resourceType === 'customDefaultImage') {
            setCustomDefaultImageResourceId(resourceId);
        } else if (resourceType === 'customHoverImage') {
            setCustomHoverImageResourceId(resourceId);
        }
    };

    // 저장 핸들러
    const handleSave = async () => {
        if (!selectedAngleId) {
            alert('앵글을 선택해주세요.');
            return;
        }

        if (!themePageId) {
            alert('페이지 정보가 올바르지 않습니다. 페이지를 다시 로드해주세요.');
            return;
        }

        setIsLoading(true);
        try {
            let result;
            
            if (isEditMode) {
                // 수정 모드
                result = await themeAngleMappingApi.updateThemeAngleMapping(
                    initialData.id,
                    {
                        customDefaultImageResourceId,
                        customHoverImageResourceId
                    }
                );
            } else {
                // 추가 모드
                result = await themeAngleMappingApi.addAngleToTheme(
                    themeId,
                    themePageId,
                    selectedAngleId,
                    {
                        defaultImageResourceId: customDefaultImageResourceId,
                        hoverImageResourceId: customHoverImageResourceId
                    }
                );
            }

            onSave(result);
            onClose();
        } catch (error) {
            console.error('저장 실패:', error);
            alert(`저장 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="modal-overlay active">
                <div className="modal-container theme-angle-modal">
                    <div className="modal-header">
                        <h3 className="modal-title">
                            테마 앵글 {isEditMode ? '수정' : '선택'}
                        </h3>
                        <button className="modal-close" onClick={onClose}>
                            <i className="ri-close-line"></i>
                        </button>
                    </div>
                    
                    <div className="modal-body">
                        <div className="modal-content">
                            {/* 앵글 선택 섹션 (추가 모드에서만) */}
                            {!isEditMode && (
                                <div className="modal-form-group">
                                    <label className="modal-label">앵글 선택</label>
                                    <div className="angle-selection-grid">
                                        {availableAngles.map((angle) => (
                                            <div 
                                                key={angle.id}
                                                className={`angle-selection-item ${selectedAngleId === angle.id ? 'selected' : ''}`}
                                                onClick={() => handleAngleSelect(angle.id)}
                                            >
                                                <div className="angle-info">
                                                    <h4>{angle.angleName}</h4>
                                                    <span className="angle-type">{angle.angleTypeDescription}</span>
                                                </div>
                                                {/*{angle.angleDataResourceId && (
                                                    <div className="angle-preview">
                                                        <img 
                                                            src={`/api/v1/resources/${angle.angleDataResourceId}/view`} 
                                                            alt={angle.angleName}
                                                            onError={(e) => {
                                                                e.target.src = '/images/angleData/default_angle.png';
                                                            }}
                                                        />
                                                    </div>
                                                )}*/}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 선택된 앵글 정보 표시 */}
                            {selectedAngleInfo && (
                                <div className="modal-form-group">
                                    <label className="modal-label">선택된 앵글 정보</label>
                                    <div className="selected-angle-info">
                                        <div className="angle-details">
                                            <h4>{selectedAngleInfo.angleName}</h4>
                                            <span className="angle-type">{selectedAngleInfo.angleTypeDescription}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 커스텀 이미지 업로드 섹션 */}
                            <div className="modal-form-group">
                                <div className="modal-row-container">
                                    <div className="modal-column">
                                        <label className="modal-label">테마별 기본 이미지</label>
                                        <ResourceUploadField
                                            label=""
                                            resourceId={customDefaultImageResourceId}
                                            onResourceUploaded={(id) => 
                                                handleResourceUploaded('customDefaultImage', id)
                                            }
                                            acceptTypes="image/*"
                                            showPreview={true}
                                        />
                                        <div className="help-text">
                                            앵글 선택 시 기본적으로 표시될 이미지입니다.
                                        </div>
                                    </div>
                                    
                                    <div className="modal-column">
                                        <label className="modal-label">테마별 호버 이미지</label>
                                        <ResourceUploadField
                                            label=""
                                            resourceId={customHoverImageResourceId}
                                            onResourceUploaded={(id) => 
                                                handleResourceUploaded('customHoverImage', id)
                                            }
                                            acceptTypes="image/*"
                                            showPreview={true}
                                        />
                                        <div className="help-text">
                                            마우스 오버 시 표시될 이미지입니다.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button 
                            className="modal-btn modal-btn-cancel" 
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            취소
                        </button>
                        <button 
                            className="modal-btn modal-btn-primary" 
                            onClick={handleSave}
                            disabled={isLoading || (!isEditMode && !selectedAngleId)}
                        >
                            {isLoading ? '저장 중...' : (isEditMode ? '수정' : '추가')}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .theme-angle-modal {
                    max-width: 800px;
                    width: 90%;
                    /* 애니메이션 충돌 방지 */
                    animation: none !important;
                    transform: none !important;
                }

                .angle-selection-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 15px;
                    margin-top: 10px;
                }

                .angle-selection-item {
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 15px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: #fff;
                    /* 레이아웃 시프트 방지 */
                    min-height: 120px;
                    display: flex;
                    flex-direction: column;
                }

                .angle-selection-item:hover {
                    border-color: #007bff;
                    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
                }

                .angle-selection-item.selected {
                    border-color: #007bff;
                    background: #f8f9ff;
                    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
                }

                .angle-info {
                    flex-shrink: 0;
                }

                .angle-info h4 {
                    margin: 0 0 5px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                }

                .angle-type {
                    font-size: 12px;
                    color: #666;
                    background: #f0f0f0;
                    padding: 2px 8px;
                    border-radius: 12px;
                }

                .angle-preview {
                    margin-top: 10px;
                    flex: 1;
                    display: flex;
                    align-items: center;
                }

                .angle-preview img {
                    width: 100%;
                    height: 80px;
                    object-fit: cover;
                    border-radius: 4px;
                    border: 1px solid #e0e0e0;
                    /* 이미지 로딩 중 레이아웃 유지 */
                    background-color: #f5f5f5;
                    display: block;
                }

                .selected-angle-info {
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    padding: 15px;
                }

                .angle-details h4 {
                    margin: 0 0 5px 0;
                    font-size: 18px;
                    color: #333;
                }

                .help-text {
                    font-size: 12px;
                    color: #666;
                    margin-top: 5px;
                    font-style: italic;
                }
            `}</style>
        </>
    );
};

export default ThemeAngleSelectionModal;
